import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const INPUTS = {
  _kiian_source: () => 'pdp_complete_the_look',
  _kiian_slot: (data) => data.slot,
  _kiian_anchor_product_id: (data) => data.anchorProductId,
  _kiian_anchor_type: (data) => data.anchorType,
  _kiian_rec_type: (data) => data.recType,
};

class CompleteTheLook extends Component {
  #abortController = null;
  #listenerController = new AbortController();
  #modalObserver = null;
  #pendingTimer = null;
  #pendingModalData = null;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#listenerController;

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener('click', this.#handleDocumentClick, { capture: true, signal });
    document.addEventListener('submit', this.#handleDocumentSubmit, { capture: true, signal });

    this.#observeQuickAddModal();
    this.#afterRender();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listenerController.abort();
    this.#abortController?.abort();
    this.#modalObserver?.disconnect();
    if (this.#pendingTimer) window.clearTimeout(this.#pendingTimer);
  }

  #handleCartUpdate = () => {
    if (this.#pendingTimer) window.clearTimeout(this.#pendingTimer);
    this.#pendingTimer = window.setTimeout(() => {
      this.#pendingTimer = null;
      this.#refresh();
    }, 140);
  };

  #handleDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const ctlCard = target.closest('[data-ctl-card]');
    if (ctlCard && this.contains(ctlCard)) {
      this.#pendingModalData = this.#dataFromCard(ctlCard);
      this.#injectCardForms(ctlCard);
      return;
    }

    if (target.closest('quick-add-component')) {
      this.#pendingModalData = null;
    }
  };

  #handleDocumentSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const ctlCard = form.closest('[data-ctl-card]');
    if (ctlCard && this.contains(ctlCard)) {
      this.#injectInputs(form, this.#dataFromCard(ctlCard));
      return;
    }

    const modal = document.getElementById('quick-add-modal-content');
    if (modal?.contains(form) && this.#pendingModalData && this.#formMatchesPendingProduct(form)) {
      this.#injectInputs(form, this.#pendingModalData);
    }
  };

  async #refresh() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('_ctl', Date.now().toString());

      const response = await fetch(url.toString(), {
        cache: 'no-store',
        signal: this.#abortController.signal,
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) return;

      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const next = html.querySelector(`complete-the-look[id="${this.id}"]`);
      if (!next) return;

      this.innerHTML = next.innerHTML;
      this.#afterRender();
    } catch (error) {
      if (error.name !== 'AbortError') return;
    } finally {
      this.#abortController = null;
    }
  }

  #afterRender() {
    this.#injectTrackingInputs();
    this.#dedupeRecommendationRail();
  }

  #injectTrackingInputs() {
    const cards = this.querySelectorAll('[data-ctl-card]');
    for (const card of cards) {
      this.#injectCardForms(card);
    }
    this.#injectQuickAddModal();
  }

  #injectCardForms(card) {
    const data = this.#dataFromCard(card);
    const forms = card.querySelectorAll('form');
    for (const form of forms) {
      if (form instanceof HTMLFormElement) this.#injectInputs(form, data);
    }
  }

  #injectQuickAddModal() {
    const modal = document.getElementById('quick-add-modal-content');
    if (!modal || !this.#pendingModalData) return;

    const forms = modal.querySelectorAll('form');
    for (const form of forms) {
      if (form instanceof HTMLFormElement && this.#formMatchesPendingProduct(form)) {
        this.#injectInputs(form, this.#pendingModalData);
      }
    }
  }

  #observeQuickAddModal() {
    const modal = document.getElementById('quick-add-modal-content');
    if (!modal) return;

    this.#modalObserver = new MutationObserver(() => this.#injectQuickAddModal());
    this.#modalObserver.observe(modal, { childList: true, subtree: true });
  }

  #injectInputs(form, data) {
    for (const [key, valueFor] of Object.entries(INPUTS)) {
      const name = `properties[${key}]`;
      const value = String(valueFor(data) ?? '');
      let input = Array.from(form.querySelectorAll('input')).find((element) => element.name === name);
      if (!(input instanceof HTMLInputElement)) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.dataset.ctlTrackingInput = 'true';
        form.appendChild(input);
      }
      input.value = value;
    }
  }

  #dataFromCard(card) {
    return {
      productId: card.getAttribute('data-ctl-product-id') || '',
      slot: card.getAttribute('data-ctl-slot') || '',
      anchorProductId: card.getAttribute('data-ctl-anchor-product-id') || '',
      anchorType: card.getAttribute('data-ctl-anchor-type') || '',
      recType: card.getAttribute('data-ctl-rec-type') || '',
    };
  }

  #formMatchesPendingProduct(form) {
    const productForm = form.closest('product-form-component');
    const productId = productForm?.getAttribute('data-product-id') || '';
    return !productId || !this.#pendingModalData?.productId || productId === this.#pendingModalData.productId;
  }

  #dedupeRecommendationRail() {
    const rail = document.querySelector('product-recommendations');
    if (!rail) return;

    const ctlIds = new Set(
      Array.from(this.querySelectorAll('[data-ctl-card]'))
        .map((card) => card.getAttribute('data-ctl-product-id'))
        .filter(Boolean)
    );

    const previous = document.querySelectorAll('[data-ctl-deduped="true"]');
    for (const item of previous) {
      item.removeAttribute('hidden');
      item.removeAttribute('data-ctl-deduped');
    }

    if (ctlIds.size === 0) return;

    const cards = rail.querySelectorAll('product-card[data-product-id]');
    for (const card of cards) {
      if (!(this.compareDocumentPosition(card) & Node.DOCUMENT_POSITION_FOLLOWING)) continue;
      if (!ctlIds.has(card.getAttribute('data-product-id'))) continue;

      const item = card.closest('.resource-list__item') || card;
      item.setAttribute('hidden', '');
      item.setAttribute('data-ctl-deduped', 'true');
    }
  }
}

if (!customElements.get('complete-the-look')) {
  customElements.define('complete-the-look', CompleteTheLook);
}
